<template>
  <expander>
    <template v-slot:title>
      <div class="rule_title flex align-items-center py-2 z-50">
        <span class="rule_status rounded-circle mr-2"
              :class="form.rule_enabled ? 'rule_status_enabled' : 'rule_status_disabled'"></span>
        <a @click.stop="move('up')" class="rule_move_button btn btn-link" :class="{'disabled': !canMoveUp}">
          <svg class="icon">
            <use xlink:href="#arrow-up"></use>
          </svg>
        </a>
        <a @click.stop="move('down')" class="rule_move_button btn btn-link" :class="{'disabled': !canMoveDown}">
          <svg class="icon">
            <use xlink:href="#arrow-down"></use>
          </svg>
        </a>
        <span class="font_bold ml-2">{{ form.alias || form.datamodel_field || 'New Business Rule' }}</span>
      </div>
    </template>
    <template v-slot:content>
      <div>
        <div
          class="border rounded px-2 my-2"
          :class="{ 'invalid-block': getRuleError() }"
        >
          <div v-if="getRuleError()" class="mt-6">
            <validation-errors :errors="getRuleError()"/>
          </div>
          <div class="form form_justify_left my-2">
            <input
              :id="`${this.id}-rule_enabled`"
              class="form_knob_check"
              type="checkbox"
              v-model="form.rule_enabled"
              :class="{ 'is-invalid': hasError('rule_enabled') }"
            />
            <label :for="`${this.id}-rule_enabled`" class="form_knob">
              <span class="label-span">Enable Business Rule</span>
              <hover-icon
                icon="question-mark-circle"
                :title="ruleEnabledTitle"
                :description="ruleEnabledDescription"
                hover
              />
            </label>
          </div>
          <div class="form form_justify_left my-2">
            <label :for="`${this.id}-execute_rule_when`" class="form_label flex justify-start">
              <span class="label-span">When to execute the Business Rule</span>
              <hover-icon
                icon="question-mark-circle"
                :title="executeRuleWhenTitle"
                :description="executeRuleWhenDescription"
                hover
              />
            </label>
            <select
              :id="`${this.id}-execute_rule_when`"
              :value="form.execute_rule_when"
              class="w-full form-control form-select mr-4 mt-2"
              :class="{ 'is-invalid': hasError('execute_rule_when') }"
              v-model="form.execute_rule_when"
            >
              <option v-for="option in executeRuleWhen" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </div>
          <div class="form form_justify_left my-2">
            <label :for="`${this.id}-datamodel_field`" class="form_label flex justify-start">
              <span class="label-span">Rule Datamodel Field</span>
              <hover-icon
                icon="question-mark-circle"
                :title="ruleDatamodelFieldTitle"
                :description="ruleDatamodelFieldDescription"
                hover
              />
            </label>
            <multiselect
              :clear-on-select="true"
              :close-on-select="true"
              :options="datamodelFields"
              :preserve-search="true"
              v-model="form.datamodel_field"
              @select="handleDatamodelFieldSelected"
              :class="{ 'is-invalid': hasError('datamodel_field') }"
            />
          </div>
          <div v-if="form.datamodel_field != null" class="form form_justify_left my-2">
            <label :for="`${this.id}-alias`" class="form_label flex justify-start">
              <span class="label-span">Business Rule Alias</span>
              <hover-icon
                icon="question-mark-circle"
                :title="aliasTitle"
                :description="aliasDescription"
                hover
              />
            </label>
            <input
              :id="`${this.id}-alias`"
              class="w-full form-control form-input form-input-bordered mr-2"
              type="text"
              v-model="form.alias"
              :class="{ 'is-invalid': hasError('alias') }"
            />
          </div>
        </div>

        <!-- Conditional Table -->
        <div
          v-if="form.datamodel_field != null"
          class="border rounded px-2 my-2"
          :class="{ 'invalid-block': getConditionalTableError() }"
        >
          <div v-if="getConditionalTableError()" class="mt-6">
            <validation-errors :errors="getConditionalTableError()"/>
          </div>
          <div class="form form_justify_left my-2">
            <input
              :id="`${this.id}-use_conditional_table`"
              class="form_knob_check"
              type="checkbox"
              v-model="form.use_conditional_table"
              :class="{ 'is-invalid': hasError('use_conditional_table') }"
            />
            <label :for="`${this.id}-use_conditional_table`" class="form_knob">
              <span class="label-span">Make use of a conditional table</span>
              <hover-icon
                icon="question-mark-circle"
                :title="useConditionalTableTitle"
                :description="useConditionalTableDescription"
                hover
              />
            </label>
          </div>
          <div v-if="form.use_conditional_table === true" class="form form_justify_left my-2">
            <label :for="`${this.id}-result_separator`" class="form_label flex justify-start">
              <span class="label-span">Result Separator</span>
              <hover-icon
                icon="question-mark-circle"
                :title="separatorTitle"
                :description="separatorDescription"
                hover
              />
            </label>
            <input
              :id="`${this.id}-result_separator`"
              :class="{ 'is-invalid': hasError('result_separator') }"
              class="w-full form-control form-input form-input-bordered mr-2"
              type="text"
              v-model="form.result_separator"
            />
          </div>
          <div v-if="form.use_conditional_table === true" class="form form_justify_left my-2">
            <label :for="`${this.id}-block_rules`" class="form_label flex justify-start">
              <span class="label-span">How should processing block behave?</span>
              <hover-icon
                icon="question-mark-circle"
                :title="blockRulesTitle"
                :description="blockRulesDescription"
                hover
              />
            </label>
            <select
              :id="`${this.id}-block_rules`"
              :value="form.block_rules"
              class="w-full form-control form-select mr-4 mt-2"
              v-model="form.block_rules"
              :class="{ 'is-invalid': hasError('block_rules') }"
            >
              <option v-for="option in blockRules" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </div>
          <div class="my-2 w-full" v-if="form.use_conditional_table">
            <ConditionalMappingTable
              :id="`${this.id}-conditionalMappingTableId`"
              :is-user-integration-flow="true"
              :mapping-tables="form.conditional_tables"
              @updated="handleUpdatedConditionalTable"
              :errors="errors"
            />
          </div>
        </div>

        <!-- Mapping Table-->
        <div
          v-if="form.datamodel_field != null"
          class="border rounded px-2 my-2"
          :class="{ 'invalid-block': getMappingTableError() }"
        >
          <div v-if="getMappingTableError()" class="mt-6">
            <validation-errors :errors="getMappingTableError()"/>
          </div>
          <div class="form form_justify_left my-2">
            <input
              :id="`${this.id}-use_mapping_table`"
              class="form_knob_check"
              type="checkbox"
              v-model="form.use_mapping_table"
              :class="{ 'is-invalid': hasError('use_mapping_table') }"
            />
            <label :for="`${this.id}-use_mapping_table`" class="form_knob">
              <span class="label-span">Make use of a mapping table </span>
              <hover-icon
                icon="question-mark-circle"
                :title="useMappingTableTitle"
                :description="useMappingTableDescription"
                hover
              />
            </label>
          </div>
          <div class="my-2 w-full" style="padding-right: 4rem" v-if="form.use_mapping_table">
            <div class="form form_justify_left my-2">
              <label :for="`${this.id}-action_not_mapped`" class="form_label flex justify-start">
                <span class="label-span">What to do with a value that cannot be mapped using the mapping table</span>
                <hover-icon
                  icon="question-mark-circle"
                  :title="actionNotMappedTitle"
                  :description="actionNotMappedDescription"
                  hover
                />
              </label>
              <select
                :id="`${this.id}-action_not_mapped`"
                :value="form.action_not_mapped"
                class="w-full form-control form-select mr-4 mt-2"
                v-model="form.action_not_mapped"
                :class="{ 'is-invalid': hasError('action_not_mapped') }"
              >
                <option v-for="option in actionsNotMapped" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </div>
            <MappingTable
              :id="`${this.id}-mappingTableId`"
              header1="Input Value"
              header2="Output Value"
              :is-user-integration-flow="true"
              :mapping-tables="form.mapping_tables"
              from-data-manipulation-options
              @updated="handleUpdatedMappingTable"
              :errors="errors"
            />
          </div>
        </div>

        <!-- Default Fields -->
        <div
          v-if="form.datamodel_field != null"
          class="border rounded px-2 my-2"
          :class="{ 'invalid-block': getDefaultError() }"
        >
          <div v-if="getDefaultError()" class="mt-6">
            <validation-errors :errors="getDefaultError()"/>
          </div>
          <div class="form form_justify_left my-2">
            <input
              :id="`${form.datamodel_field}${this.id}-default_usage`"
              class="form_knob_check"
              type="checkbox"
              v-model="form.default_usage"
              :class="{ 'is-invalid': hasError('default_usage') }"
            />
            <label :for="`${form.datamodel_field}${this.id}-default_usage`" class="form_knob">
              <span class="label-span">Make use of a default value</span>
              <hover-icon
                icon="question-mark-circle"
                :title="useDefaultTitle"
                :description="useDefaultDescription"
                hover
              />
            </label>
          </div>
          <div v-if="form.default_usage" class="form form_justify_left my-2">
            <input
              :id="`${this.id}-default_forced`"
              class="form_knob_check"
              type="checkbox"
              v-model="form.default_forced"
              :class="{ 'is-invalid': hasError('default_forced') }"
            />
            <label :for="`${this.id}-default_forced`" class="form_knob">
              <span class="label-span">Force The Usage Of The Default</span>
              <hover-icon
                icon="question-mark-circle"
                :title="forceDefaultTitle"
                :description="forceDefaultDescription"
                hover
              />
            </label>
          </div>
          <div v-if="form.default_usage" class="form form_justify_left my-2">
            <label :for="`${this.id}-use_default_for`" class="form_label flex justify-start">
              <span class="label-span">Use Default For</span>
              <hover-icon
                icon="question-mark-circle"
                :title="useDefaultForTitle"
                :description="useDefaultForDescription"
                hover
              />
            </label>
            <select
              :id="`${this.id}-use_default_for`"
              :value="form.use_default_for"
              class="w-full form-control form-select mr-4 mt-2"
              v-model="form.use_default_for"
              :class="{ 'is-invalid': hasError('use_default_for') }"
            >
              <option v-for="option in useDefaultFor" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </div>
          <div v-if="form.default_usage" class="form form_justify_left my-2">
            <label :for="`${this.id}-default_value`" class="form_label flex justify-start">
              <span class="label-span">Value to use as a default</span>
              <hover-icon
                icon="question-mark-circle"
                :title="defaultValueTitle"
                :description="defaultValueDescription"
                hover
              />
            </label>
            <input
              :id="`${this.id}-default_value`"
              class="w-full form-control form-input form-input-bordered mr-2"
              type="text"
              v-model="form.default_value"
              :class="{ 'is-invalid': hasError('default_value') }"
            />
          </div>
        </div>

        <!-- Microfunction-->
        <div
          v-if="form.datamodel_field != null"
          class="border rounded px-2 my-2"
          :class="{ 'invalid-block': getMicrofunctionError() }"
        >
          <div v-if="getMicrofunctionError()" class="mt-6">
            <validation-errors :errors="getMicrofunctionError()"/>
          </div>
          <div class="form form_justify_left my-2">
            <input
              :id="`${this.id}-microfunction_usage`"
              class="form_knob_check"
              type="checkbox"
              v-model="form.microfunction_usage"
              :class="{ 'is-invalid': hasError('microfunction_usage') }"
            />
            <label :for="`${this.id}-microfunction_usage`" class="form_knob">
              <span class="label-span">Make use of a micro function</span>
              <hover-icon
                icon="question-mark-circle"
                :title="microfunctionUsageTitle"
                :description="microfunctionUsageDescription"
                hover
              />
            </label>
          </div>
          <div v-if="form.microfunction_usage" class="form form_justify_left my-2">
            <label :for="`${this.id}-microfunction`" class="form_label flex justify-start">
              <span class="label-span">Microfunction</span>
              <hover-icon
                icon="question-mark-circle"
                :title="microfunctionTitle"
                :description="microfunctionDescription"
                hover
              />
            </label>
            <div class="flex-input">
              <multiselect
                v-model="form.microfunction"
                :id="`${this.id}-microfunction`"
                :options="microfunctions"
                :multiple="false"
                placeholder="Select microfunction"
                :clear-on-select="false"
                :close-on-select="true"
                :preserve-search="true"
                :value="form.microfunction"
                :class="{ 'is-invalid': hasError('microfunction') }"
              />
            </div>
            <div class="form form_justify_left my-2">
              <label :for="`${this.id}-use_microfunction_when`" class="form_label flex justify-start">
                <span class="label-span">When to execute the microfunction</span>
                <hover-icon
                  icon="question-mark-circle"
                  :title="useMicrofunctionWhenTitle"
                  :description="useMicrofunctionWhenDescription"
                  hover
                />
              </label>
              <select
                :id="`${this.id}-use_microfunction_when`"
                :value="form.use_microfunction_when"
                class="w-full form-control form-select mr-4 mt-2"
                :class="{ 'is-invalid': hasError('use_microfunction_when') }"
                v-model="form.use_microfunction_when"
              >
                <option v-for="option in useMicrofunctionWhen" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </div>
      <div class="flex mt-2">
        <button
          v-if="$can('flows.business-rules.create|flows.business-rules.update')"
          type="button"
          class="button"
          @click="saveBusinessRule"
        >
          Save Business Rule Block
        </button>
        <button
          v-if="$can('flows.business-rules.delete')"
          type="button"
          class="button button_danger"
          @click="showConfirmationModal = true"
        >
          Delete Business Rule Block
        </button>
        <button
          v-if="isAdmin && this.id !== null"
          class="button button_success"
          type="button"
          v-text="trans('Store as template')"
          @click="showStoreTemplateModal = true"
        />
      </div>
    </template>
  </expander>
  <confirmation-modal
    :show-modal="showConfirmationModal"
    :header="trans('dashboard.integrations.flows.confirm_action')"
    :content="trans('dashboard.integrations.flows.business_rules.delete_business_rule_block_confirmation_description')"
    :confirm-button-text="trans('common.confirm')"
    @close="showConfirmationModal = false"
    @confirm="deleteBusinessRule"
  />
  <flow-store-template-modal
    v-if="isAdmin && this.id !== null"
    :show="showStoreTemplateModal"
    :user-integration-id="userIntegration.id"
    :user-integration-flow-id="userIntegrationFlow.id"
    type="business_rule"
    :payload="{business_rule_id: this.id}"
    @close="showStoreTemplateModal = false"
  />
</template>

<script>
import Expander from '@/components/Dashboard/Expander';
import Multiselect from 'vue-multiselect';
import MappingTable from '@/components/Dashboard/MappingTable';
import ConditionalMappingTable from '@/components/Dashboard/ConditionalMappingTable';
import {mapGetters} from 'vuex';
import HoverIcon from '@/components/Shared/HoverIcon';
import Swal from 'sweetalert2';
import ConfirmationModal from '@/components/Dashboard/ConfirmationModal.vue';
import ValidationErrors from "@/components/Shared/ValidationErrors.vue";
import FlowStoreTemplateModal from "@/Pages/Dashboard/Integrations/Flows/Partials/FlowStoreTemplateModal.vue";

export default {
  emits: ['update:modelValue', 'deleted', 'move'],
  name: 'DataManipulationBlock',
  components: {
    ValidationErrors,
    ConfirmationModal,
    HoverIcon,
    Expander,
    Multiselect,
    MappingTable,
    ConditionalMappingTable,
    FlowStoreTemplateModal,
  },
  props: {
    modelValue: {
      type: Object,
      required: true,
    },
    canMoveUp: {
      type: Boolean,
      default: false,
    },
    canMoveDown: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {
        datamodel_field: this.modelValue.datamodel_field || null,
        alias: this.modelValue.alias || null,
        rule_enabled: this.modelValue.rule_enabled || false,
        execute_rule_when: this.modelValue.execute_rule_when || 2,
        default_usage: this.modelValue.default_usage || false,
        default_forced: this.modelValue.default_forced || false,
        default_value: this.modelValue.default_value || null,
        use_mapping_table: this.modelValue.use_mapping_table || false,
        use_conditional_table: this.modelValue.use_conditional_table || false,
        action_not_mapped: this.modelValue.action_not_mapped || null,
        mapping_tables: this.modelValue.mapping_tables || [],
        conditional_tables: this.modelValue.conditional_tables || [],
        use_default_for: this.modelValue.use_default_for || null,
        microfunction_usage: this.modelValue.microfunction_usage || false,
        microfunction: this.modelValue.microfunction || null,
        use_microfunction_when: this.modelValue.use_microfunction_when || null,
        result_separator: this.modelValue.result_separator || "|",
        block_rules: this.modelValue.block_rules || 1,
      },
      errors: null,
      showConfirmationModal: false,
      showStoreTemplateModal: false,
      ruleDatamodelFieldTitle: "<h3>Rule Datamodel Field</h3>",
      ruleDatamodelFieldDescription: '<p>This is the field in the APIcenter datamodel to which the <u>final</u> outcome ' +
        'of the entire Business Rule will be written (or not) depending on the configuration of the rule.<br><br>Select ' +
        'a field from the list and click it to select it.</p><br>' +
        '<p>After each Business Rule has been processed the result value is written to the [Rule Datamodel Field]. ' +
        'This means that the next business rule can also be applied to the same datamodel field, using the result value ' +
        'from the previous Business Rule.</p>',
      aliasTitle: '<h3>Alias</h3>',
      aliasDescription: '<p>An alias can be supplied to make the title of the business rule more logical and ' +
        'understandable if desired.<p>',
      ruleEnabledTitle: '<h3>Enable Business Rule</h3>',
      ruleEnabledDescription: '<p>Enable/disable Business Rule</p>',
      executeRuleWhenTitle: '<h3>When to execute the Business Rule</h3>',
      executeRuleWhenDescription: '<p>Select when to execute the Business Rule. This can be before any of the enrichment ' +
        'is done by APIcenter or after all the enrichment is done.</p>',
      useDefaultTitle: '<h3>Default Value Usage</h3>',
      useDefaultDescription: '<p>By enabling this setting the usage of a default value becomes available. Additional ' +
        'options like if the default should be forced and more will become available.</p>',
      forceDefaultTitle: '<h3>Force Default Value</h3>',
      forceDefaultDescription: '<p>When enabled the default value will always be applied even when there is a value ' +
        'present in the data. If disabled the default will only be applied in case no value is present in the data.</p>',
      useConditionalTableTitle: '<h3>Conditional Table Usage</h3>',
      useDefaultForTitle: '<h3>Use Default Value For</h3>',
      useDefaultForDescription: '<p>Select for which purpose(s) the default value should be used.</p>',
      defaultValueTitle: '<h3>Default Value</h3>',
      defaultValueDescription: '<p>Enter the value that should be used as the default value.</p>',
      useConditionalTableDescription: '<p>With a conditional table it is possible to influence the data processed by using ' +
        'conditions that should be met before a defined action is applied.</p><br>' +
        '<p>Before starting to create conditions it is important to understand how the conditions are applied. ' +
        'With a conditional table it is possible to influence the data processed by using ' +
        'Conditional tables exist of `blocks` that contain `rows` that can either be part of a grouped condition or a ' +
        'single condition depending on any brackets used or not. Blocks are processes in the order of listing and handled ' +
        'from top to bottom. Within a block the conditions are processed in the same way.</p><br>' +
        '<h5>Blocks:</h5>' +
        '<p></p><br>' +
        '<h5>Important:</h5>' +
        '<div class="ml-4"><ul><li>When the first condition of block results in `FALSE` and there are multiple blocks ' +
        'defined within the conditional table, the rest of the block will be skipped and the next block will be processed.</li>' +
        '</ul></div>' +
        '<h5>Rows:</h5>' +
        '<p></p><br>' +
        '<h5>Conditions:</h5>' +
        '<p></p><br>',
      separatorTitle: '<h3>Result Separator</h3>',
      separatorDescription: '<p>When a block has multiple conditions, this is the character that will be used as the ' +
        'seperator between the results of each condition. <br><br>For example: when there are 2 conditions present in a ' +
        'block, that both have [Action](Use Result Value) set, this will result in a block result of `TRUE|TRUE` ' +
        '(or any combination of `TRUE` and `FALSE`) when the default separator `|` is used.</p>',
      blockRulesTitle: '<h3>How processing of block should behave</h3>',
      blockRulesDescription: '<p>The way the blocks in a conditional table are processed can make a difference on the ' +
        'final outcome of a business rule or result in unexpected behaviour. To prevent that from happening this setting ' +
        'can be used to set how the blocks should be processed in case a condition is `true`.<br><br>' +
        'Please keep in mind that when the 1st condition of a block is `false`, it will always go to the next block, ' +
        'unless there is only 1 block or when it is the last block being processed and the action is set to use a value</p>' +
        '<table class="table"><tr><th>Rule</th><th>Description</th></tr>' +
        '<tr><td>Skip next blocks after condition is `true`</td>' +
        '<td>This is the default value for this setting and the normal behaviour of how blocks are processes. In this ' +
        'case the processing of blocks will stop when the condition is `true` and the action of the condition is applied ' +
        '</td></tr><tr><td>Go to next block after condition is `true`</td>' +
        '<td>When the condition in a block is `true` it will execute the action for the conditions. ' +
        'When done it will go to the next block and skip it in case the first condition is `false`, or process it when ' +
        'the condition is `true`. This process is repeated until all blocks in de conditional table are processed.' +
        '</td></tr></table>',
      useMappingTableTitle: '<h3>Mapping Table Usage</h3>',
      useMappingTableDescription: '<p>Make use of a mapping table to map values between applications or between the ' +
        'business rules and application.</p>',
      actionNotMappedTitle: '<h3>Action Not Mapped</h3>',
      actionNotMappedDescription: '<p>What action should be taken when a value in the data cannot be mapped against ' +
        'a value in the mapping table.</p>',
      microfunctionUsageTitle: '<h3>Micro-Function Usage</h3>',
      microfunctionUsageDescription: '<p>After <u>all</u> configured defaults, conditional tables and mapping tables have been ' +
        'processed, a micro-function may be required to change the format of the final outcome of the Business Rule, so ' +
        'it can be written to the [Rule Datamodel Field].<br><br>This can come in handy when the outcome of ' +
        'a Business Rule is a number constructed from several values or fields that need to be used as a single ' +
        'integer on the revieving application side. Or a Business Rule where calculations were performed but the ' +
        'outcome should be of type `string` for the SEND-applicaion</p>',
      microfunctionTitle: '<h3>Micro-Function</h3>',
      microfunctionDescription: '<p>Select a micro-function from the list to be executed.</p>',
      useMicrofunctionWhenTitle: '<h3>Execute Micro-Function when?</h3>',
      useMicrofunctionWhenDescription: '<p>Select when the micro-function should be executed.<br><br>' +
        'For example:<br>When the result of the conditional table happens to start or end with a space, you might want ' +
        'to trim the value first using the micro-function, before attempting to map the value using a mapping table. ' +
        'In such a case select `Before mapping` from the dropdown.</p>',
    };
  },
  computed: {
    ...mapGetters('flowsDetails', [
      'isAdmin',
      'flowGroup',
      'userIntegration',
      'userIntegrationFlow',
      'datamodelFields',
      'actionsNotMapped',
      'useDefaultFor',
      'microfunctions',
      'applyActionTo',
      'actions',
      'useMicrofunctionWhen',
      'executeRuleWhen',
      'blockRules'
    ]),

    id() {
      return this.modelValue.id || this.form.id || null;
    },

    application1() {
      return this.userIntegration.first_application.application;
    },

    application2() {
      return this.userIntegration.second_application.application;
    },
  },
  methods: {
    handleDatamodelFieldSelected(datamodel_field) {
      this.form.alias = datamodel_field;
    },
    saveBusinessRule() {
      if (this.id === null) {
        this.storeBusinessRule();

        return;
      }

      this.updateBusinessRule();
    },
    storeBusinessRule() {
      axios
        .post(`/api/integrations/${this.userIntegration.id}/flows/${this.userIntegrationFlow.id}/business-rules`, this.form)
        .then(({data}) => {
          this.updateFormData(data.data);
          this.notify(this.trans('flows.Successfully stored Business Rule options.'));
        })
        .catch(({response}) => {
          if (response.status === 403) {
            this.notify(response.data.message, 'error');

            return;
          }

          this.errors = response.data.errors;
          this.notify(this.trans('validation.Please fill out all required fields'), 'error');
        });
    },
    updateBusinessRule() {
      axios
        .patch(`/api/integrations/${this.userIntegration.id}/flows/${this.userIntegrationFlow.id}/business-rules/${this.id}`, this.form)
        .then(({data}) => {
          this.updateFormData(data.data);
          this.notify(this.trans('flows.Successfully updated Business Rule options.'));
        })
        .catch(({response}) => {
          if (response.status === 403) {
            this.notify(response.data.message, 'error');

            return;
          }
          this.errors = response.data.errors;
          this.notify(this.trans('validation.Please fill out all required fields'), 'error');
        });
    },
    async deleteBusinessRule() {
      this.$emit('deleted', this.id);

      if (this.id != null) {
        await axios
          .delete(`/api/integrations/${this.userIntegration.id}/flows/${this.userIntegrationFlow.id}/business-rules/${this.id}`)
          .then(() => this.notify(this.trans('flows.Successfully deleted Business Rule.')))
          .catch(({response}) => {
            if (response.status === 403) {
              this.notify(response.data.message, 'error');
            }
          });
      }
    },
    notify(text, icon = 'success') {
      Swal.fire({
        text: text,
        icon: icon,
        showConfirmButton: false,
        timer: 2000,
      });
    },
    updateFormData(data) {
      const actions = _.flatMap(this.actions, 'options');

      data.conditional_tables = _.map(data.conditional_tables, table => {
        return _.map(table, row => {
          row.action = _.find(actions, actionOption => actionOption.value === row.action)

          return row
        });
      });

      this.form = _.pick(data, [
        'id',
        'datamodel_field',
        'alias',
        'rule_enabled',
        'execute_rule_when',
        'default_usage',
        'default_forced',
        'default_value',
        'use_mapping_table',
        'use_conditional_table',
        'result_separator',
        'action_not_mapped',
        'mapping_tables',
        'conditional_tables',
        'use_default_for',
        'microfunction_usage',
        'microfunction',
        'use_microfunction_when',
        'block_rules'
      ]);
    },
    handleUpdatedMappingTable(mappingTables) {
      this.form.mapping_tables = mappingTables;
    },
    handleUpdatedConditionalTable(conditionalTables) {
      this.form.conditional_tables = conditionalTables;
    },
    getRuleError() {
      const ruleErrors = _.uniq(
        _.flatMap(_.filter(this.errors, (error, key) => String(key).includes(`datamodel`) || String(key).includes(`alias`))),
      );

      if (ruleErrors.length > 0) {
        return ruleErrors;
      }

      return null;
    },
    getMicrofunctionError() {
      const microErrors = _.uniq(
        _.flatMap(_.filter(this.errors, (error, key) => String(key).includes('microfunction'))),
      );
      if (microErrors.length > 0) {
        return microErrors;
      }

      return null;
    },
    getDefaultError() {
      const defaultErrors = _.uniq(
        _.flatMap(_.filter(this.errors, (error, key) => String(key).includes(`default`))),
      );

      if (defaultErrors.length > 0) {
        return defaultErrors;
      }

      return null;
    },
    getConditionalTableError() {
      const tableErrors = _.uniq(
        _.flatMap(_.filter(this.errors, (error, key) => String(key).includes(`separator`)))
      );

      if (tableErrors.length > 0) {
        return tableErrors;
      }

      return null;
    },
    getMappingTableError() {
      const tableErrors = _.uniq(
        _.flatMap(_.filter(this.errors, (error, key) => String(key).includes(`map`))),
      );

      if (tableErrors.length > 0) {
        return tableErrors;
      }

      return null;
    },
    hasError(field) {
      return this.errors?.[field];
    },

    move(direction) {
      const canMove = direction === 'up' ? this.canMoveUp : this.canMoveDown;

      if (!canMove) {
        return
      }

      this.$emit('move', direction);
    },
  },
  watch: {
    form: {
      deep: true,
      handler(current) {
        this.$emit('update:modelValue', {...current, id: this.id});
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.invalid-block, .form_field .is-invalid {
  border-color: #f43737 !important;
}

.form-input {
  padding: 0 12px 0 12px !important;
}

.label-span {
  padding-right: 5px;
}

.form_knob.popper {
  width: 800px !important;
}

.Expander__trigger.active .rule_move_button:not(.disabled),
.Expander__trigger:hover .rule_move_button:not(.disabled) {
  color: rgba(255, 255, 255, 0.9);
}

.Expander__trigger.active .rule_move_button.disabled,
.Expander__trigger:hover .rule_move_button.disabled {
  color: rgba(0, 0, 0, 0.2);
}

.rule_title .icon {
  width: 1.5em;
  height: 1.5em;
}

.rule_status {
  width: 1em;
  height: 1em;
}

.rule_status.rule_status_enabled {
  background: green;
}

.rule_status.rule_status_disabled {
  background: red;
}
</style>
